import Hls, { Fragment, FragmentLoaderConstructor, HlsConfig, HlsListeners } from 'hls.js';

import { IContentControlVideoSource } from './i-content-control-video-source';
import { ITimestampToTimelineConverter } from './provider/i-timestamp-to-timeline-converter';
import { CustomFragmentLoader } from './provider/custom-fragment-loader';
import { FormatNumber } from '../common/ui-utils';

/**
 * Configuration for creating an {@link HlsStreamVideoSource} via its
 * {@link HlsStreamVideoSource.create|create} factory.
 */
export interface HlsStreamVideoSourceConfig {
  /** The HTML video element that HLS.js will attach to for media playback. */
  videoElement: HTMLVideoElement;
  /** The URL of the HLS manifest (`.m3u8`) to load. */
  hlsStreamUrl: string;
  /** Frame rate of the video stream (frames per second). */
  fps: number;
  /** Whether to enable HLS.js debug logging. Defaults to `false`. */
  debug?: boolean;
}

/**
 * Callbacks the consumer registers to react to HLS lifecycle events.
 * All callbacks are optional — the source handles its own lifecycle
 * (error recovery, buffer config, fragment tracking) regardless.
 */
export interface HlsStreamLifecycleCallbacks {
  /** Fired after the HLS manifest has been parsed and initial metadata extracted. */
  onManifestReady?: () => void;
  /** Fired when the HLS.js audio tracks list is updated from the manifest. */
  onAudioTracksUpdated?: () => void;
  /** Fired when the HLS.js subtitle tracks list is updated from the manifest. */
  onSubtitleTracksUpdated?: () => void;
  /** Fired when a level's playlist is loaded or updated with new fragment data. */
  onLevelUpdated?: () => void;
  /** Fired on an unrecoverable HLS error (not network or media). */
  onFatalError?: (errorType: string, data: unknown) => void;
}

/**
 * Self-contained video source backed by an HLS.js instance.
 *
 * Owns the full HLS lifecycle: instance creation, source loading, media
 * attachment, error recovery, manifest parsing, buffer reconfiguration,
 * fragment tracking, and seeking.
 *
 * Implements {@link IContentControlVideoSource} for use by the studio
 * canvas and playback controller.
 */
export class HlsStreamVideoSource implements IContentControlVideoSource, ITimestampToTimelineConverter {

  // ---------------------------------------------------------------------------
  // Constants
  // ---------------------------------------------------------------------------

  /** Estimated bytes per HLS segment, used for buffer size calculation. */
  private static readonly BYTES_PER_SEGMENT = 2 * 1024 * 1024;

  // ---------------------------------------------------------------------------
  // Logging
  // ---------------------------------------------------------------------------

  /** Whether console logging is enabled for this instance. */
  private _printEnabled = true;

  // ---------------------------------------------------------------------------
  // Frame rate
  // ---------------------------------------------------------------------------

  /** Current frame rate of the video stream (frames per second). */
  private _videoFps = 0;
  /** Duration of a single frame in seconds, derived from {@link _videoFps}. */
  private _oneFrameSecs = 0;

  // ---------------------------------------------------------------------------
  // HLS lifecycle state
  // ---------------------------------------------------------------------------

  /** Whether the HLS manifest has been parsed and the stream metadata is available. */
  private _manifestParsed = false;

  /** The currently active HLS fragment, updated via FRAG_CHANGED / LEVEL_LOADED. */
  private _activeFrag: Fragment | null = null;

  /** Timestamp (ms since epoch) when the manifest was parsed. */
  private _loadTimeMs = 0;

  /** Target segment duration from the manifest (seconds). */
  private _segmentDurationSecs = 5;

  /** Number of fragments in the playlist after manifest parse. */
  private _segmentsCount = 10;

  /** Maximum backlog buffer duration (seconds), derived from total playlist duration. */
  private _maxBacklogBufferSecs = 30;

  /**
   * Stable reference timestamp (ms) captured once when the manifest is first parsed.
   * Prevents drift as the HLS sliding window advances and `firstFragment` changes.
   */
  private _playlistStartTimestampMs = 0;

  /** Registered lifecycle callbacks from the consumer (view model). */
  private _lifecycleCallbacks: HlsStreamLifecycleCallbacks = {};

  // ---------------------------------------------------------------------------
  // Factory
  // ---------------------------------------------------------------------------

  /**
   * Creates a fully initialised HLS video source: creates the HLS.js instance,
   * loads the manifest URL, attaches the video element, sets up error handling,
   * and registers manifest / fragment tracking listeners.
   *
   * Prefer this over the raw constructor for live-mode usage.
   */
  public static create(config: HlsStreamVideoSourceConfig): HlsStreamVideoSource {
    const hlsConfig: Partial<HlsConfig> = {
      debug: config.debug ?? false,
      lowLatencyMode: true,
      startPosition: 0,
      startLevel: 0,
      renderTextTracksNatively: true,
      maxBufferSize: 30 * 2 * 1024 * 1024,
      maxBufferLength: 30,
      maxMaxBufferLength: 30,
      backBufferLength: 30,
      enableSoftwareAES: false,
      testBandwidth: false,
      progressive: false,
      liveSyncDuration: 0,
      liveDurationInfinity: true,
      enableWorker: true,
      startFragPrefetch: false,
      fLoader: CustomFragmentLoader as unknown as FragmentLoaderConstructor,
    };

    const hls = new Hls(hlsConfig);
    hls.loadSource(config.hlsStreamUrl);
    hls.attachMedia(config.videoElement);

    const source = new HlsStreamVideoSource(hls);
    source._printEnabled = config.debug ?? source._printEnabled;
    source.setupFrameRate(config.fps);
    source._setupErrorHandling();
    source._setupManifestParsing();
    return source;
  }

  // ---------------------------------------------------------------------------
  // Constructor
  // ---------------------------------------------------------------------------

  /**
   * Creates a video source backed by an HLS.js instance.
   * The caller must attach media (`hls.attachMedia(video)`) before constructing
   * this wrapper, so that `_hlsLibrary.media` is already set.
   *
   * Prefer {@link HlsStreamVideoSource.create} for full lifecycle management.
   */
  constructor(private readonly _hlsLibrary: Hls) {}

  // ---------------------------------------------------------------------------
  // Private element accessors
  // ---------------------------------------------------------------------------

  /** Returns the HTMLVideoElement attached to the HLS.js instance. */
  private get _videoElement(): HTMLVideoElement {
    return this._hlsLibrary.media as HTMLVideoElement;
  }

  /** Returns the first fragment of the first HLS level, or `null` if not yet loaded. */
  private get _firstFragment() {
    return this._hlsLibrary.levels[0]?.details?.fragments?.[0] ?? null;
  }

  // ---------------------------------------------------------------------------
  // IContentControlVideoSource — frame rate
  // ---------------------------------------------------------------------------

  /** Frame rate of the video stream (frames per second). */
  public get videoFps(): number { return this._videoFps; }
  /** Duration of a single frame in seconds. */
  public get oneFrameSecs(): number { return this._oneFrameSecs; }

  /**
   * Configures the frame rate and recalculates the single-frame duration.
   * @param fps  Frames per second of the video stream
   */
  public setupFrameRate(fps: number): void {
    this._videoFps = fps;
    this._oneFrameSecs = +(1 / fps).toPrecision(12);
  }

  /**
   * Converts a time position to its corresponding zero-based frame index.
   * Uses precision rounding to avoid floating-point boundary errors.
   *
   * @param timeSecs  Time in seconds; defaults to {@link currentTimeSecs}
   * @returns Zero-based frame index at the given time
   */
  public currentPlayTimeAsFrameIndex(timeSecs?: number): number {
    timeSecs = timeSecs ?? this.currentTimeSecs;
    const fn = timeSecs * this._videoFps;
    let frameIndex = Math.floor(fn);
    if (+(fn - frameIndex).toPrecision(12) >= this._oneFrameSecs) {
      frameIndex++;
    }
    return frameIndex;
  }

  /**
   * Snaps a time position to the start of the nearest frame boundary in seconds.
   *
   * @param timeSecs  Time in seconds; defaults to {@link currentTimeSecs}
   * @returns Frame-aligned time in seconds, or `0` if `timeSecs` is `0`
   */
  public currentPlayTimeAsClosestFrameSecs(timeSecs?: number): number {
    if (timeSecs === 0) return 0;
    const frameIndex = this.currentPlayTimeAsFrameIndex(timeSecs);
    return frameIndex / this._videoFps;
  }

  /**
   * Snaps a time position to the start of the nearest frame boundary in milliseconds.
   *
   * @param timeSecs  Time in seconds; defaults to {@link currentTimeSecs}
   * @returns Frame-aligned time in milliseconds, or `0` if `timeSecs` is `0`
   */
  public currentPlayTimeAsClosestFrameMs(timeSecs?: number): number {
    if (timeSecs === 0) return 0;
    const frameIndex = this.currentPlayTimeAsFrameIndex(timeSecs);
    return FormatNumber((frameIndex / this._videoFps) * 1000);
  }

  // ---------------------------------------------------------------------------
  // IContentControlVideoSource — time & state
  // ---------------------------------------------------------------------------

  /**
   * Current playback position as a UTC timestamp in seconds.
   * Prefers `hls.playingDate`; falls back to extrapolating from the first
   * fragment's `programDateTime`. Returns `0` if neither source is available.
   */
  public get currentTimeSecs(): number {
    const playingDate = this._hlsLibrary.playingDate;
    if (playingDate) {
      return playingDate.getTime() / 1000;
    }

    const firstFrag = this._firstFragment;
    if (firstFrag?.programDateTime != null) {
      return (firstFrag.programDateTime + (this._videoElement.currentTime - firstFrag.start) * 1000) / 1000;
    }

    return 0;
  }

  /** Raw player-relative current time in seconds (not UTC-adjusted). */
  public get rawCurrentTimeSecs(): number { return this._videoElement.currentTime; }
  /** Sets the raw player-relative current time in seconds. */
  public set rawCurrentTimeSecs(value: number) { this._videoElement.currentTime = value; }

  /** Whether the video element is muted. */
  public get muted(): boolean { return this._videoElement.muted; }
  /** Mutes or unmutes the video element. */
  public set muted(value: boolean) { this._videoElement.muted = value; }

  /** Current playback speed multiplier (e.g. `1.0` = normal, `2.0` = double). */
  public get playbackRate(): number { return this._videoElement.playbackRate; }
  /** Sets the playback speed multiplier. */
  public set playbackRate(value: number) { this._videoElement.playbackRate = value; }

  /** Width of the video element in pixels. */
  public get widthPx(): number { return this._videoElement.width; }
  /** Sets the width of the video element in pixels. */
  public set widthPx(value: number) { this._videoElement.width = value; }

  /** Height of the video element in pixels. */
  public get heightPx(): number { return this._videoElement.height; }
  /** Sets the height of the video element in pixels. */
  public set heightPx(value: number) { this._videoElement.height = value; }

  /** Total duration of the media in seconds (may be `Infinity` for live streams). */
  public get durationSecs(): number { return this._videoElement.duration; }
  /** Whether the video element is currently paused. */
  public get paused(): boolean { return this._videoElement.paused; }
  /** The HTMLMediaElement ready state (0–4), indicating how much data is available. */
  public get readyState(): number { return this._videoElement.readyState; }

  // ---------------------------------------------------------------------------
  // IContentControlVideoSource — playback
  // ---------------------------------------------------------------------------

  /** Starts or resumes video playback. Resolves when playback actually begins. */
  public play(): Promise<void> { return this._videoElement.play(); }
  /** Pauses video playback. */
  public pause(): void { this._videoElement.pause(); }
  /** Resets the video element and reloads the media resource. */
  public load(): void { this._videoElement.load(); }

  /**
   * Seeks the HLS player to a target position (player-relative seconds),
   * clamped to the current playlist window. Stops and restarts HLS loading
   * at the target position so the decoder picks up the correct segment.
   *
   * Falls back gracefully (no-op) if level details or fragments are not yet available.
   *
   * @param seekTimeSecs  Target time in player-relative seconds
   */
  public seekToVideoPlayerRelativeTimeInSecs(seekTimeSecs: number): void {
    if (!this.hasLevelDetails) return;

    const first = this.firstFragment;
    const last = this.lastFragment;
    if (!first || !last) return;

    const playlistEndSecs = last.start + last.duration;
    const clamped = Math.max(
      first.start,
      Math.min(seekTimeSecs, playlistEndSecs),
    );

    this._log('log', 'seekToVideoPlayerRelativeTimeInSecs | target:', seekTimeSecs, 'clamped:', clamped);
    this.stopLoad();
    this.rawCurrentTimeSecs = clamped;
    this.startLoad(clamped);
  }

  // ---------------------------------------------------------------------------
  // IContentControlVideoSource — events
  // ---------------------------------------------------------------------------

  /**
   * Registers a DOM event listener on the underlying video element.
   *
   * @param type      Event name (e.g. `'timeupdate'`, `'ended'`)
   * @param listener  Event handler callback
   * @param options   Standard `addEventListener` options (capture, passive, etc.)
   */
  public addEventListener(type: string, listener: EventListenerOrEventListenerObject, options?: boolean | AddEventListenerOptions): void {
    this._videoElement.addEventListener(type, listener, options);
  }

  /**
   * Removes a DOM event listener from the underlying video element.
   *
   * @param type      Event name matching a prior {@link addEventListener} call
   * @param listener  The same handler reference passed to {@link addEventListener}
   */
  public removeEventListener(type: string, listener: EventListenerOrEventListenerObject): void {
    this._videoElement.removeEventListener(type, listener);
  }

  // ---------------------------------------------------------------------------
  // IContentControlVideoSource — source management
  // ---------------------------------------------------------------------------

  /**
   * Loads a new HLS source URL into the player, replacing any current source.
   * @param url  HLS manifest URL (`.m3u8`)
   */
  public addSource(url: string): void {
    this._hlsLibrary.loadSource(url);
  }

  /**
   * No-op for HLS streams — audio tracks are managed automatically via the
   * HLS manifest. Required by {@link IContentControlVideoSource}.
   */
  public addAudioTrack(_url: string, _language: string): void {
    // No-op — HLS.js manages audio tracks via manifest
  }

  /** Detaches the video element from the HLS.js instance, stopping playback. */
  public clearSource(): void {
    this._hlsLibrary.detachMedia();
  }

  /**
   * Returns the video element as a {@link TexImageSource} for WebGL texture uploads.
   * Used by the studio canvas to render video frames onto a WebGL surface.
   */
  public getTexImageSource(): TexImageSource {
    return this._videoElement as TexImageSource;
  }

  // ---------------------------------------------------------------------------
  // HLS-specific accessors (not part of IContentControlVideoSource)
  // ---------------------------------------------------------------------------

  /** All fragments in the first HLS level, or an empty array if not yet loaded. */
  public get fragments(): Fragment[] {
    return this._hlsLibrary.levels[0]?.details?.fragments ?? [];
  }

  /** First fragment in the HLS playlist, or null if not yet loaded. */
  public get firstFragment(): Fragment | null {
    const frags = this.fragments;
    return frags.length > 0 ? frags[0] : null;
  }

  /** Last fragment in the HLS playlist, or null if not yet loaded. */
  public get lastFragment(): Fragment | null {
    const frags = this.fragments;
    return frags.length > 0 ? frags[frags.length - 1] : null;
  }

  /** End time of the last fragment in player seconds, or 0 if no fragments. */
  public get playlistEndTimeSecs(): number {
    const last = this.lastFragment;
    return last ? last.start + last.duration : 0;
  }

  /** End time of the last fragment as a UTC timestamp in milliseconds, or 0 if no fragments. */
  public get playlistEndTimeAsTimestampMs(): number {
    return this.convertPlayerRelativeTimeToUtcTimestampMs(this.playlistEndTimeSecs);
  }
    /** End time of the last fragment as a UTC timestamp in seconds, or 0 if no fragments. */
    public get playlistEndTimeAsTimestampSecs(): number {
      return this.playlistEndTimeAsTimestampMs / 1000;
    }

  /** Whether the HLS playlist has loaded at least one fragment. */
  public get hasFragments(): boolean {
    return this.fragments.length > 0;
  }

  /** Whether the first HLS level has parsed playlist details. */
  public get hasLevelDetails(): boolean {
    return this._hlsLibrary.levels[0]?.details != null;
  }

  /** Live sync position in player seconds, or undefined if unavailable. */
  public get liveSyncPosition(): number | undefined {
    return this._hlsLibrary.liveSyncPosition;
  }

  /** The date corresponding to the current playback position, or null. */
  public get playingDate(): Date | null {
    return this._hlsLibrary.playingDate;
  }

  /** Maximum buffer length from HLS.js config (seconds). */
  public get maxBufferLengthSecs(): number {
    return this._hlsLibrary.config.maxBufferLength;
  }

  /** Subtitle track names from the HLS manifest. */
  public get subtitleTrackNames(): string[] {
    return this._hlsLibrary.subtitleTracks?.map((t) => t.name) ?? [];
  }

  /** Audio track names from the HLS manifest. */
  public get audioTrackNames(): string[] {
    return this._hlsLibrary.audioTracks?.map((t) => t.name) ?? [];
  }

  /** Target segment duration from the first level (seconds). */
  public get levelTargetDuration(): number {
    return this._hlsLibrary.levels[0]?.details?.targetduration ?? 0;
  }

  /** Number of fragments in the first level's playlist. */
  public get levelFragmentCount(): number {
    return this._hlsLibrary.levels[0]?.details?.fragments?.length ?? 0;
  }

  /** Total duration of all fragments in the first level (seconds). */
  public get levelTotalDuration(): number {
    return this._hlsLibrary.levels[0]?.details?.totalduration ?? 0;
  }

  // ---------------------------------------------------------------------------
  // HLS lifecycle state (public getters)
  // ---------------------------------------------------------------------------

  /** Whether the HLS manifest has been parsed. */
  public get isManifestParsed(): boolean { return this._manifestParsed; }

  /** The currently active HLS fragment, or null before any fragment loads. */
  public get activeFrag(): Fragment | null { return this._activeFrag; }

  /** Timestamp (ms since epoch) when the manifest was parsed. */
  public get loadTimeMs(): number { return this._loadTimeMs; }

  /** Target segment duration from the manifest (seconds). */
  public get segmentDurationSecs(): number { return this._segmentDurationSecs; }

  /** Number of fragments in the playlist after manifest parse. */
  public get segmentsCount(): number { return this._segmentsCount; }

  /**
   * Stable playlist start timestamp (ms) captured once at manifest parse.
   * Use this instead of `firstFragment.programDateTime` to avoid drift
   * as the HLS sliding window advances.
   */
  public get playlistStartTimestampMs(): number { return this._playlistStartTimestampMs; }

  /**
   * Computes the UTC timestamp (ms) of the current playback position.
   * Tries three sources in order of accuracy:
   * 1. `hls.playingDate` (HLS.js built-in, most accurate)
   * 2. Active fragment's `programDateTime` + offset
   * 3. Fragment-walk extrapolation
   * Falls back to `Date.now()` if the stream lacks `programDateTime` tags.
   */
  public get currentVideoTimeUtcMs(): number {
    const playingDate = this._hlsLibrary.playingDate;
    if (playingDate) return playingDate.getTime();

    const fromActiveFragMs = this._activeFrag
      ? this.convertPlayerRelativeTimeToUtcTimestampMs()
      : 0;
    if (fromActiveFragMs > 0) return fromActiveFragMs;

    const fallbackMs = this._computeUtcMsFromFragments();
    if (fallbackMs > 0) return fallbackMs;

    return Date.now();
  }

  // ---------------------------------------------------------------------------
  // HLS-specific methods (not part of IContentControlVideoSource)
  // ---------------------------------------------------------------------------

  /** Registers an event listener on the underlying HLS.js instance. */
  public onHlsEvent<E extends keyof HlsListeners>(event: E, listener: HlsListeners[E]): void {
    this._hlsLibrary.on(event, listener);
  }

  /** Stops loading of HLS segments. */
  public stopLoad(): void {
    this._hlsLibrary.stopLoad();
  }

  /** Starts or resumes loading of HLS segments from an optional position. */
  public startLoad(startPosition?: number): void {
    this._hlsLibrary.startLoad(startPosition);
  }

  /** Attempts to recover from a media error. */
  public recoverMediaError(): void {
    this._hlsLibrary.recoverMediaError();
  }

  /** Merges partial config overrides into the HLS.js config. */
  public updateConfig(overrides: Partial<HlsConfig>): void {
    Object.assign(this._hlsLibrary.config, overrides);
  }

  /**
   * Registers lifecycle callbacks so the consumer can react to HLS events.
   * Must be called before MANIFEST_PARSED fires for the callbacks to take effect.
   */
  public registerLifecycleCallbacks(callbacks: HlsStreamLifecycleCallbacks): void {
    this._lifecycleCallbacks = callbacks;
  }

  /**
   * Converts a player-time position (seconds) to a UTC timestamp (milliseconds)
   * using the active fragment's `programDateTime`.
   *
   * @param timeSecs  Player time in seconds; defaults to {@link rawCurrentTimeSecs}
   * @returns UTC timestamp in milliseconds, or 0 if no active fragment
   */
  public convertPlayerRelativeTimeToUtcTimestampMs(timeSecs?: number): number {
    if (!this._activeFrag || this._activeFrag.programDateTime == null) return 0;
    timeSecs = timeSecs ?? this.rawCurrentTimeSecs;
    const deltaMs = (timeSecs - this._activeFrag.start) * 1000;
    return this._activeFrag.programDateTime + deltaMs;
  }

  /**
   * Converts a UTC timestamp (ms) to a position on the timeline (player seconds),
   * clamped to the current playlist window.
   *
   * @param timestampMs  UTC timestamp in milliseconds
   * @returns Player seconds within the playlist window
   */
  public timestampUtcToPlayerRelativeTimeInSeconds(timestampMs: number): number {
    const fragments = this.fragments;
    if (!fragments.length) return 0;
    const lastFrag = fragments[fragments.length - 1];
    const offsetFromLiveSecs = (lastFrag.programDateTime - timestampMs) / 1000;
    const positionSecs = lastFrag.start - offsetFromLiveSecs;
    const playlistStartSecs = fragments[0].start;
    return Math.max(playlistStartSecs, Math.min(positionSecs, lastFrag.start + lastFrag.duration));
  }

  /**
   * Computes the UTC timestamp (ms) from HLS fragments for the current playback
   * position. Walks fragments to find the one containing `currentTime` and
   * extrapolates from its `programDateTime`.
   *
   * @returns UTC timestamp in ms, or 0 if unable to compute
   */
  private _computeUtcMsFromFragments(): number {
    if (!this.hasFragments) return 0;
    const fragments = this.fragments;
    const currentTimeSecs = this.rawCurrentTimeSecs;
    if (!Number.isFinite(currentTimeSecs)) return 0;

    for (const frag of fragments) {
      if (
        frag.programDateTime != null &&
        currentTimeSecs >= frag.start &&
        currentTimeSecs < frag.start + frag.duration
      ) {
        return frag.programDateTime + (currentTimeSecs - frag.start) * 1000;
      }
    }

    const first = this.firstFragment;
    const last = this.lastFragment;
    if (first?.programDateTime != null && currentTimeSecs < first.start) {
      return first.programDateTime + (currentTimeSecs - first.start) * 1000;
    }
    if (last?.programDateTime != null && currentTimeSecs >= last.start) {
      return last.programDateTime + (currentTimeSecs - last.start) * 1000;
    }
    // Fallback for gaps between fragments: extrapolate from first fragment
    if (first?.programDateTime != null) {
      return first.programDateTime + (currentTimeSecs - first.start) * 1000;
    }
    return 0;
  }

  /** Pauses media, removes all listeners, and destroys the HLS.js instance. */
  public destroy(): void {
    if (this._hlsLibrary.media) {
      this._hlsLibrary.media.pause();
    }
    this._hlsLibrary.removeAllListeners();
    this._hlsLibrary.destroy();
  }

  // ---------------------------------------------------------------------------
  // Private: HLS lifecycle setup
  // ---------------------------------------------------------------------------

  /**
   * Registers the HLS.js error event handler with automatic recovery for
   * network and media errors. Fatal errors of other types are reported via
   * the {@link HlsStreamLifecycleCallbacks.onFatalError} callback.
   */
  private _setupErrorHandling(): void {
    this._hlsLibrary.on(Hls.Events.ERROR, (_, data) => {
      if (data.fatal) {
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            this.startLoad();
            break;
          case Hls.ErrorTypes.MEDIA_ERROR:
            this.recoverMediaError();
            break;
          default:
            this._lifecycleCallbacks.onFatalError?.(data.type, data);
            break;
        }
      }
    });
  }

  /**
   * Registers the MANIFEST_PARSED, AUDIO_TRACKS_UPDATED, and
   * SUBTITLE_TRACKS_UPDATED listeners. On manifest parse, extracts
   * metadata, reconfigures buffers, registers ongoing fragment/level
   * listeners, and fires {@link HlsStreamLifecycleCallbacks.onManifestReady}.
   */
  private _setupManifestParsing(): void {
    this._hlsLibrary.on(Hls.Events.AUDIO_TRACKS_UPDATED, () => {
      this._lifecycleCallbacks.onAudioTracksUpdated?.();
    });
    this._hlsLibrary.on(Hls.Events.SUBTITLE_TRACKS_UPDATED, () => {
      this._lifecycleCallbacks.onSubtitleTracksUpdated?.();
    });

    const onManifestParsed = () => {
      this._handleManifestParsed();
    };
    this._hlsLibrary.on(Hls.Events.MANIFEST_PARSED, onManifestParsed);

    // If fragments are already loaded (fast stream), handle immediately
    if (this.hasFragments) {
      onManifestParsed();
    }
  }

  /**
   * Handles manifest-parsed: extracts fragment metadata, reconfigures HLS.js
   * buffer sizes, registers ongoing LEVEL_LOADED / FRAG_CHANGED / LEVEL_UPDATED
   * listeners, and notifies the consumer via {@link HlsStreamLifecycleCallbacks.onManifestReady}.
   */
  private _handleManifestParsed(): void {
    if (this._manifestParsed) return;
    this._manifestParsed = true;
    this._loadTimeMs = Date.now();
    this._playlistStartTimestampMs = this.firstFragment?.programDateTime ?? 0;

    if (this.hasLevelDetails) {
      const lastFrag = this.lastFragment;
      if (lastFrag) {
        this._activeFrag = lastFrag;
      }
      this._segmentDurationSecs = this.levelTargetDuration;
      this._segmentsCount = this.levelFragmentCount;
      this._maxBacklogBufferSecs = this.levelTotalDuration;
      this.updateConfig({
        maxBufferLength: this._maxBacklogBufferSecs,
        maxBufferSize: this._segmentsCount * HlsStreamVideoSource.BYTES_PER_SEGMENT,
        maxMaxBufferLength: this._maxBacklogBufferSecs,
        backBufferLength: this._maxBacklogBufferSecs,
      });
    }

    // Ongoing fragment / level tracking
    this._hlsLibrary.on(Hls.Events.LEVEL_LOADED, (_, data) => {
      const frags = data.details?.fragments;
      if (frags?.length && !this._activeFrag) {
        this._activeFrag = frags[frags.length - 1];
      }
      const totalDuration = data.details?.totalduration ?? 0;
      if (totalDuration > 0 && totalDuration > this._maxBacklogBufferSecs) {
        this._segmentDurationSecs = data.details?.targetduration ?? this._segmentDurationSecs;
        this._segmentsCount = frags?.length ?? this._segmentsCount;
        this._maxBacklogBufferSecs = totalDuration;
        this.updateConfig({
          maxBufferLength: this._maxBacklogBufferSecs,
          maxBufferSize: this._segmentsCount * HlsStreamVideoSource.BYTES_PER_SEGMENT,
          maxMaxBufferLength: this._maxBacklogBufferSecs,
          backBufferLength: this._maxBacklogBufferSecs,
        });
        this._lifecycleCallbacks.onLevelUpdated?.();
      }
    });
    this._hlsLibrary.on(Hls.Events.FRAG_CHANGED, (_, data) => {
      const { frag } = data;
      if (frag.type === 'main') {
        this._activeFrag = frag;
      }
    });
    this._hlsLibrary.on(Hls.Events.LEVEL_UPDATED, (_, data) => {
      if (data.details) {
        this._lifecycleCallbacks.onLevelUpdated?.();
      }
    });

    this._lifecycleCallbacks.onManifestReady?.();
  }

  // ---------------------------------------------------------------------------
  // Private: logging
  // ---------------------------------------------------------------------------

  /**
   * Logs a styled message to the console when {@link _printEnabled} is `true`.
   *
   * @param level    Console method to use (`'log'`, `'warn'`, or `'error'`)
   * @param message  Values to log after the styled prefix
   */
  private _log(level: 'log' | 'warn' | 'error', ...message: unknown[]): void {
    if (this._printEnabled) {
      console[level](
        '%c[HLS-SOURCE] ',
        'background: #222; color: #bada55',
        ...message,
      );
    }
  }
}
